import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, FooterActionBar } from '@saviynt/design-system';

import { useLocalization } from '../../../../hooks';
import { useTypedSelector } from '../../../../hooks/use-typedselector';
import Logger from '../../../../services/loggerService';
import { type StartImportRequest } from '../../data-layer/types';
import { useStartImportMutation } from '../../data-store/DiscoveryFileUploadApi';
import {
  resetDiscoveryFileUploadState,
  setIsStartImport,
  setStartImportResponse,
} from '../../data-store/DiscoveryFileUploadSlice';

import './FileUploadPreviewTableFooter.css';

/**
 * Footer component for the file upload preview table that contains action buttons
 * for navigating back and starting the import process.
 *
 * @param {Object} props - Component props
 * @param {number|string|null} props.fileUploadIobAppId - The IOB application ID for file upload
 * @param {number|string|null} props.fileUploadSourceAppId - The source application ID for file upload
 */
function FileUploadPreviewTableFooter({
  fileUploadIobAppId,
  fileUploadSourceAppId,
}: {
  fileUploadIobAppId: number | string | null;
  fileUploadSourceAppId: number | string | null;
}): React.ReactNode {
  const dispatch = useDispatch();
  const formatMessage = useLocalization();

  const [startImport] = useStartImportMutation();

  const criticalCount = useTypedSelector(
    (state) => state.discoveryFileUpload.criticalCount
  );

  const criticalMessage = useTypedSelector(
    (state) => state.discoveryFileUpload.criticalMessage
  );

  const updatedHeaders = useTypedSelector(
    (state) => state.discoveryFileUpload.updatedHeaders
  );

  const fileUploadResult = useTypedSelector(
    (state) => state.discoveryFileUpload.data
  );

  const handleBackButton = () => {
    dispatch(resetDiscoveryFileUploadState());
  };

  const handleStartImport = async () => {
    const firstUpload = fileUploadResult[0];
    const { fileName } = firstUpload;
    const { generatedFileName } = firstUpload;

    dispatch(setIsStartImport(true));

    if (!fileName || !generatedFileName || fileUploadSourceAppId == null) {
      Logger.error('Missing required data to start import');

      return;
    }

    const payload: StartImportRequest = {
      appTypeKey: fileUploadSourceAppId.toString(),
      iobAppKey:
        fileUploadIobAppId != null ? fileUploadIobAppId.toString() : null,
      fileName,
      schemaFileNames: generatedFileName,
      updatedHeader: updatedHeaders,
    };

    try {
      const response = await startImport(payload).unwrap();

      dispatch(setStartImportResponse(response));
    } catch (err) {
      Logger.error('Start import failed:', err);
      const errorResponse =
        err instanceof Error
          ? {
              status: 500,
              message: err.message,
            }
          : (err as { data?: { status: number; message: string } }).data ?? {
              status: 500,
              message: 'Unknown error',
            };

      dispatch(setStartImportResponse(errorResponse));
    }
  };

  return (
    <FooterActionBar size='large' className='AOB-ModalIsland-footer'>
      <Box className='AOB-ModalIsland-footer-buttons'>
        <Button
          onClick={handleBackButton}
          kind='subtle'
          size='medium'
          dataTestId='aob-fileupload-previewtable-back'>
          {formatMessage('INTEGRATIONS.DISCOVERY.BACK', 'Back')}
        </Button>
        <Button
          kind='filled'
          size='medium'
          onClick={handleStartImport}
          isDisabled={criticalCount > 0 || criticalMessage.length > 0}
          dataTestId='aob-fileupload-previewtable-startimport'>
          {formatMessage('INTEGRATIONS.FILEUPLOAD.STARTIMPORT', 'Start Import')}
        </Button>
      </Box>
    </FooterActionBar>
  );
}

export default FileUploadPreviewTableFooter;
